import * as XLSX from 'xlsx';
import { MedicalCondition, PDPMRankingAnalysis } from '../types/codeIdentifier';

interface ExportData {
  finalEvaluation: MedicalCondition[];
  pdpmRankingAnalysis: PDPMRankingAnalysis | null;
  patientNames: string;
  generatedBy?: string;
  facilityName?: string;
}

export class ExportService {
  // Data cleaning helpers
  private cleanTextValue(value: any): string {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'object' && value !== null) {
      // Try to extract meaningful text from object
      if (value.toString && value.toString() !== '[object Object]') {
        return value.toString();
      }
      // Return a fallback message for objects
      return 'Data available in system';
    }
    return String(value || '');
  }

  private cleanImpact(impact: any): string {
    const cleaned = this.cleanTextValue(impact);
    
    // Similar cleaning for impact fields
    const patterns = [
      /Database lookup:\s*/i,
      /vector_search_score=[\d.]+\s*/g,
      /impact=.*?(?=\s|$)/g
    ];
    
    let result = cleaned;
    patterns.forEach(pattern => {
      result = result.replace(pattern, '');
    });
    
    return result.trim() || 'Impact assessment available';
  }
  public exportCategoricalToCSV(data: ExportData): void {
    const csvData = this.prepareCategoricalData(data);
    const csvContent = this.convertToCSV(csvData);
    this.downloadFile(csvContent, this.generateFileName(data.patientNames, 'Categorical', 'csv'), 'text/csv');
  }

  public exportCategoricalToExcel(data: ExportData): void {
    const categoricalData = this.prepareCategoricalData(data);
    const workbook = XLSX.utils.book_new();
    
    // Main data sheet
    const worksheet = XLSX.utils.json_to_sheet(categoricalData);
    this.autoSizeColumns(worksheet, categoricalData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Categorical Analysis');
    
    // Summary sheet
    const summaryData = this.prepareCategoricalSummary(data);
    const summarySheet = XLSX.utils.json_to_sheet(summaryData);
    this.autoSizeColumns(summarySheet, summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
    
    XLSX.writeFile(workbook, this.generateFileName(data.patientNames, 'Categorical', 'xlsx'));
  }

  public exportPDPMToCSV(data: ExportData): void {
    if (!data.pdpmRankingAnalysis) {
      throw new Error('No PDPM ranking analysis data available');
    }
    
    const csvData = this.preparePDPMData(data);
    const csvContent = this.convertToCSV(csvData);
    this.downloadFile(csvContent, this.generateFileName(data.patientNames, 'PDPM', 'csv'), 'text/csv');
  }

  public exportPDPMToExcel(data: ExportData): void {
    if (!data.pdpmRankingAnalysis) {
      throw new Error('No PDPM ranking analysis data available');
    }
    
    // Helper function to get all codes from a condition (handles both single and multiple codes)
    const getCodesFromCondition = (condition: any) => {
      if (condition.best_icd10_codes && condition.best_icd10_codes.length > 0) {
        return condition.best_icd10_codes;
      }
      // Fallback for backward compatibility
      if (condition.best_icd10_code) {
        return [condition.best_icd10_code];
      }
      return [];
    };

    // Helper function to get code description
    const getCodeDescription = (code: string): string => {
      for (const condition of data.finalEvaluation) {
        const codes = getCodesFromCondition(condition);
        const foundCode = codes.find((c: any) => c.code === code);
        if (foundCode) {
          return foundCode.short_description || 'Description not available';
        }
      }
      return 'Description not available';
    };
    
    const workbook = XLSX.utils.book_new();
    
    // Primary Diagnosis sheet
    if (data.pdpmRankingAnalysis.primary_diagnosis_recommendation) {
      const code = data.pdpmRankingAnalysis.primary_diagnosis_recommendation.code;
      const codeString = typeof code === 'string' ? code : code.code;
      const primaryData = [{
        'Type': 'Primary Diagnosis Recommendation',
        'ICD-10 Code': codeString,
        'Code Description': typeof code === 'string' ? getCodeDescription(codeString) : code.short_description || 'N/A',
        'Clinical Category': typeof code === 'string' ? 'N/A' : code.clinical_category || 'N/A',
        'Justification': data.pdpmRankingAnalysis.primary_diagnosis_recommendation.justification
      }];
      
      // Add alternatives
      data.pdpmRankingAnalysis.primary_diagnosis_alternatives.forEach((alt, index) => {
        const code = alt.code;
        const codeString = typeof code === 'string' ? code : code.code;
        primaryData.push({
          'Type': `Alternative ${index + 1}`,
          'ICD-10 Code': codeString,
          'Code Description': typeof code === 'string' ? getCodeDescription(codeString) : code.short_description || 'N/A',
          'Clinical Category': typeof code === 'string' ? 'N/A' : code.clinical_category || 'N/A',
          'Justification': alt.justification
        });
      });
      
      const primarySheet = XLSX.utils.json_to_sheet(primaryData);
      this.autoSizeColumns(primarySheet, primaryData);
      XLSX.utils.book_append_sheet(workbook, primarySheet, 'Primary Diagnosis');
    }
    
    // Secondary Diagnoses sheet
    if (data.pdpmRankingAnalysis.non_primary_diagnoses.length > 0) {
      const nonPrimaryData = data.pdpmRankingAnalysis.non_primary_diagnoses.map(item => {
        const code = item.code;
        const codeString = typeof code === 'string' ? code : code.code;
        return {
          'ICD-10 Code': codeString,
          'Code Description': typeof code === 'string' ? getCodeDescription(codeString) : code.short_description || 'N/A',
          'Clinical Description': item.description,
          'Complexity Impact': this.cleanImpact(item.complexity_impact),
          'Reimbursement Impact': this.cleanImpact(item.reimbursement_impact),
          'Should Document': item.should_document ? 'Yes' : 'No'
        };
      });
      
      const nonPrimarySheet = XLSX.utils.json_to_sheet(nonPrimaryData);
      this.autoSizeColumns(nonPrimarySheet, nonPrimaryData);
      XLSX.utils.book_append_sheet(workbook, nonPrimarySheet, 'Secondary Diagnoses');
    }
    
    // NTA Codes sheet
    if (data.pdpmRankingAnalysis.nta_codes.length > 0) {
      const ntaData = data.pdpmRankingAnalysis.nta_codes.map(item => {
        const code = item.code;
        const codeString = typeof code === 'string' ? code : code.code;
        return {
          'ICD-10 Code': codeString,
          'Code Description': typeof code === 'string' ? getCodeDescription(codeString) : code.short_description || 'N/A',
          'NTA Points': item.nta_points,
          'Reasoning': item.reasoning
        };
      });
      
      const ntaSheet = XLSX.utils.json_to_sheet(ntaData);
      this.autoSizeColumns(ntaSheet, ntaData);
      XLSX.utils.book_append_sheet(workbook, ntaSheet, 'NTA Codes');
    }
    
    // SLP Codes sheet
    if (data.pdpmRankingAnalysis.slp_codes.length > 0) {
      const slpData = data.pdpmRankingAnalysis.slp_codes.map(item => {
        const code = item.code;
        const codeString = typeof code === 'string' ? code : code.code;
        return {
          'ICD-10 Code': codeString,
          'Code Description': typeof code === 'string' ? getCodeDescription(codeString) : code.short_description || 'N/A',
          'SLP Relevance': this.cleanImpact(item.slp_relevance),
          'Impact': this.cleanImpact(item.impact)
        };
      });
      
      const slpSheet = XLSX.utils.json_to_sheet(slpData);
      this.autoSizeColumns(slpSheet, slpData);
      XLSX.utils.book_append_sheet(workbook, slpSheet, 'SLP Codes');
    }
    
    // Summary sheet
    const summaryData = this.preparePDPMSummary(data);
    const summarySheet = XLSX.utils.json_to_sheet(summaryData);
    this.autoSizeColumns(summarySheet, summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
    
    XLSX.writeFile(workbook, this.generateFileName(data.patientNames, 'PDPM', 'xlsx'));
  }

  public exportAllToExcel(data: ExportData): void {
    const workbook = XLSX.utils.book_new();
    
    // Helper function to get code description
    const getCodeDescription = (code: string): string => {
      for (const condition of data.finalEvaluation) {
        const codes = condition.best_icd10_codes || [];
        const foundCode = codes.find(c => c.code === code);
        if (foundCode) {
          return foundCode.short_description || 'Description not available';
        }
      }
      return 'Description not available';
    };
    
    // Categorical Analysis sheet
    const categoricalData = this.prepareCategoricalData(data);
    const categoricalSheet = XLSX.utils.json_to_sheet(categoricalData);
    this.autoSizeColumns(categoricalSheet, categoricalData);
    XLSX.utils.book_append_sheet(workbook, categoricalSheet, 'Categorical Analysis');
    
    // PDPM sections (if available)
    if (data.pdpmRankingAnalysis) {
      // Primary Diagnosis sheet
      if (data.pdpmRankingAnalysis.primary_diagnosis_recommendation) {
        const code = data.pdpmRankingAnalysis.primary_diagnosis_recommendation.code;
        const codeString = typeof code === 'string' ? code : code.code;
        const primaryData = [{
          'Type': 'Primary Diagnosis Recommendation',
          'ICD-10 Code': codeString,
          'Code Description': typeof code === 'string' ? getCodeDescription(codeString) : code.short_description || 'N/A',
          'Clinical Category': typeof code === 'string' ? 'N/A' : code.clinical_category || 'N/A',
          'Justification': data.pdpmRankingAnalysis.primary_diagnosis_recommendation.justification
        }];
        
        data.pdpmRankingAnalysis.primary_diagnosis_alternatives.forEach((alt, index) => {
          const code = alt.code;
          const codeString = typeof code === 'string' ? code : code.code;
          primaryData.push({
            'Type': `Alternative ${index + 1}`,
            'ICD-10 Code': codeString,
            'Code Description': typeof code === 'string' ? getCodeDescription(codeString) : code.short_description || 'N/A',
            'Clinical Category': typeof code === 'string' ? 'N/A' : code.clinical_category || 'N/A',
            'Justification': alt.justification
          });
        });
        
        const primarySheet = XLSX.utils.json_to_sheet(primaryData);
        this.autoSizeColumns(primarySheet, primaryData);
        XLSX.utils.book_append_sheet(workbook, primarySheet, 'PDPM Primary');
      }
      
      // Secondary Diagnoses
      if (data.pdpmRankingAnalysis.non_primary_diagnoses.length > 0) {
        const nonPrimaryData = data.pdpmRankingAnalysis.non_primary_diagnoses.map(item => {
          const code = item.code;
          const codeString = typeof code === 'string' ? code : code.code;
          return {
            'ICD-10 Code': codeString,
            'Code Description': typeof code === 'string' ? getCodeDescription(codeString) : code.short_description || 'N/A',
            'Clinical Description': item.description,
            'Complexity Impact': this.cleanImpact(item.complexity_impact),
            'Reimbursement Impact': this.cleanImpact(item.reimbursement_impact),
            'Should Document': item.should_document ? 'Yes' : 'No'
          };
        });
        
        const nonPrimarySheet = XLSX.utils.json_to_sheet(nonPrimaryData);
        this.autoSizeColumns(nonPrimarySheet, nonPrimaryData);
        XLSX.utils.book_append_sheet(workbook, nonPrimarySheet, 'PDPM Non-Primary');
      }
      
      // NTA Codes
      if (data.pdpmRankingAnalysis.nta_codes.length > 0) {
        const ntaData = data.pdpmRankingAnalysis.nta_codes.map(item => {
          const code = item.code;
          const codeString = typeof code === 'string' ? code : code.code;
          return {
            'ICD-10 Code': codeString,
            'Code Description': typeof code === 'string' ? getCodeDescription(codeString) : code.short_description || 'N/A',
            'NTA Points': item.nta_points,
            'Reasoning': item.reasoning
          };
        });
        
        const ntaSheet = XLSX.utils.json_to_sheet(ntaData);
        this.autoSizeColumns(ntaSheet, ntaData);
        XLSX.utils.book_append_sheet(workbook, ntaSheet, 'PDPM NTA');
      }
      
      // SLP Codes
      if (data.pdpmRankingAnalysis.slp_codes.length > 0) {
        const slpData = data.pdpmRankingAnalysis.slp_codes.map(item => {
          const code = item.code;
          const codeString = typeof code === 'string' ? code : code.code;
          return {
            'ICD-10 Code': codeString,
            'Code Description': typeof code === 'string' ? getCodeDescription(codeString) : code.short_description || 'N/A',
            'SLP Relevance': this.cleanImpact(item.slp_relevance),
            'Impact': this.cleanImpact(item.impact)
          };
        });
        
        const slpSheet = XLSX.utils.json_to_sheet(slpData);
        this.autoSizeColumns(slpSheet, slpData);
        XLSX.utils.book_append_sheet(workbook, slpSheet, 'PDPM SLP');
      }
    }
    
    // Overall Summary sheet
    const summaryData = this.prepareOverallSummary(data);
    const summarySheet = XLSX.utils.json_to_sheet(summaryData);
    this.autoSizeColumns(summarySheet, summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
    
    XLSX.writeFile(workbook, this.generateFileName(data.patientNames, 'Complete', 'xlsx'));
  }

  private prepareCategoricalData(data: ExportData) {
    // Helper function to get all codes from a condition
    const getCodesFromCondition = (condition: any) => {
      if (condition.best_icd10_codes && condition.best_icd10_codes.length > 0) {
        return condition.best_icd10_codes;
      }
      // Fallback for backward compatibility
      if (condition.best_icd10_code) {
        return [condition.best_icd10_code];
      }
      return [];
    };

    const result: any[] = [];
    data.finalEvaluation.forEach(condition => {
      const codes = getCodesFromCondition(condition);
      if (codes.length === 0) {
        result.push({
          'Category': condition.category || 'Uncategorized',
          'Condition Name': condition.condition_name,
          'Condition Description': condition.condition_description,
          'ICD-10 Code': 'No Code',
          'Code Description': 'N/A',
          'AI Reasoning': 'N/A',
          'Current Status': condition.current_condition_status || 'N/A',
          'Provider Notes': condition.snippets?.join(' | ') || 'N/A'
        });
      } else {
        codes.forEach((code: any, index: number) => {
          result.push({
            'Category': condition.category || 'Uncategorized',
            'Condition Name': index === 0 ? condition.condition_name : '', // Only show condition name for first code
            'Condition Description': index === 0 ? condition.condition_description : '',
            'ICD-10 Code': code.code || 'No Code',
            'Code Description': code.short_description || 'N/A',
            'AI Reasoning': code.reasoning || 'N/A',
            'Current Status': index === 0 ? (condition.current_condition_status || 'N/A') : '',
            'Provider Notes': index === 0 ? (condition.snippets?.join(' | ') || 'N/A') : ''
          });
        });
      }
    });
    return result;
  }

  private preparePDPMData(data: ExportData) {
    if (!data.pdpmRankingAnalysis) return [];
    
    // Helper function to get all codes from a condition
    const getCodesFromCondition = (condition: any) => {
      if (condition.best_icd10_codes && condition.best_icd10_codes.length > 0) {
        return condition.best_icd10_codes;
      }
      // Fallback for backward compatibility
      if (condition.best_icd10_code) {
        return [condition.best_icd10_code];
      }
      return [];
    };

    // Create a helper function to get code description from categorical data
    const getCodeDescription = (code: string): string => {
      for (const condition of data.finalEvaluation) {
        const codes = getCodesFromCondition(condition);
        const foundCode = codes.find((c: any) => c.code === code);
        if (foundCode) {
          return foundCode.short_description || 'Description not available';
        }
      }
      return 'Description not available';
    };
    
    const pdpmData: any[] = [];
    
    // Primary diagnosis recommendation
    if (data.pdpmRankingAnalysis.primary_diagnosis_recommendation) {
      const code = data.pdpmRankingAnalysis.primary_diagnosis_recommendation.code;
      const codeString = typeof code === 'string' ? code : code.code;
      pdpmData.push({
        'Section': 'Primary Diagnosis',
        'Type': 'Recommendation',
        'ICD-10 Code': codeString,
        'Code Description': typeof code === 'string' ? getCodeDescription(codeString) : code.short_description || 'N/A',
        'Clinical Category': typeof code === 'string' ? 'N/A' : code.clinical_category || 'N/A',
        'Justification': data.pdpmRankingAnalysis.primary_diagnosis_recommendation.justification,
        'Value': '',
        'Impact': ''
      });
    }
    
    // Primary diagnosis alternatives
    data.pdpmRankingAnalysis.primary_diagnosis_alternatives.forEach((alt, index) => {
      const code = alt.code;
      const codeString = typeof code === 'string' ? code : code.code;
      pdpmData.push({
        'Section': 'Primary Diagnosis',
        'Type': `Alternative ${index + 1}`,
        'ICD-10 Code': codeString,
        'Code Description': typeof code === 'string' ? getCodeDescription(codeString) : code.short_description || 'N/A',
        'Clinical Category': typeof code === 'string' ? 'N/A' : code.clinical_category || 'N/A',
        'Justification': alt.justification,
        'Value': `Rank ${alt.rank}`,
        'Impact': ''
      });
    });
    
    // Non-primary diagnoses
    data.pdpmRankingAnalysis.non_primary_diagnoses.forEach(item => {
      const code = item.code;
      const codeString = typeof code === 'string' ? code : code.code;
      pdpmData.push({
        'Section': 'Secondary Diagnoses',
        'Type': 'Diagnosis',
        'ICD-10 Code': codeString,
        'Code Description': typeof code === 'string' ? getCodeDescription(codeString) : code.short_description || 'N/A',
        'Clinical Category': item.description,
        'Justification': `Complexity: ${this.cleanImpact(item.complexity_impact)}`,
        'Value': item.should_document ? 'Document' : 'Optional',
        'Impact': this.cleanImpact(item.reimbursement_impact)
      });
    });
    
    // NTA codes
    data.pdpmRankingAnalysis.nta_codes.forEach(item => {
      const code = item.code;
      const codeString = typeof code === 'string' ? code : code.code;
      pdpmData.push({
        'Section': 'NTA Codes',
        'Type': 'NTA Qualifying',
        'ICD-10 Code': codeString,
        'Code Description': typeof code === 'string' ? getCodeDescription(codeString) : code.short_description || 'N/A',
        'Clinical Category': '',
        'Justification': item.reasoning,
        'Value': `${item.nta_points} points`,
        'Impact': ''
      });
    });
    
    // SLP codes
    data.pdpmRankingAnalysis.slp_codes.forEach(item => {
      const code = item.code;
      const codeString = typeof code === 'string' ? code : code.code;
      pdpmData.push({
        'Section': 'SLP Codes',
        'Type': 'SLP Related',
        'ICD-10 Code': codeString,
        'Code Description': typeof code === 'string' ? getCodeDescription(codeString) : code.short_description || 'N/A',
        'Clinical Category': this.cleanImpact(item.slp_relevance),
        'Justification': '',
        'Value': '',
        'Impact': this.cleanImpact(item.impact)
      });
    });
    
    return pdpmData;
  }

  private prepareCategoricalSummary(data: ExportData) {
    // Helper function to get all codes from a condition
    const getCodesFromCondition = (condition: any) => {
      if (condition.best_icd10_codes && condition.best_icd10_codes.length > 0) {
        return condition.best_icd10_codes;
      }
      // Fallback for backward compatibility
      if (condition.best_icd10_code) {
        return [condition.best_icd10_code];
      }
      return [];
    };

    // Count conditions with at least one valid code
    const conditionsWithValidCodes = data.finalEvaluation.filter(condition => {
      const codes = getCodesFromCondition(condition);
      return codes.some((code: any) => 
        code.code && !['NO_CODE', 'UNKNOWN', 'No Code', 'N/A'].includes(code.code)
      );
    }).length;
    
    // Count total unique valid codes
    const allValidCodes = new Set<string>();
    data.finalEvaluation.forEach(condition => {
      const codes = getCodesFromCondition(condition);
      codes.forEach((code: any) => {
        if (code.code && !['NO_CODE', 'UNKNOWN', 'No Code', 'N/A'].includes(code.code)) {
          allValidCodes.add(code.code);
        }
      });
    });
    
    const totalConditions = data.finalEvaluation.length;
    const categoriesCount = new Set(
      data.finalEvaluation.map(condition => condition.category || 'Uncategorized')
    ).size;
    
    return [
      { 'Metric': 'Patient(s)', 'Value': data.patientNames || 'Unknown' },
      { 'Metric': 'Total Medical Conditions Identified', 'Value': totalConditions },
      { 'Metric': 'Conditions with Valid ICD-10 Codes', 'Value': conditionsWithValidCodes },
      { 'Metric': 'Total Unique ICD-10 Codes Found', 'Value': allValidCodes.size },
      { 'Metric': 'Medical Categories', 'Value': categoriesCount },
      { 'Metric': 'Success Rate', 'Value': `${Math.round((conditionsWithValidCodes / totalConditions) * 100)}%` },
      { 'Metric': 'Generated By', 'Value': data.generatedBy || 'CodePilot AI' },
      { 'Metric': 'Generated On', 'Value': new Date().toLocaleDateString() },
    ];
  }

  private preparePDPMSummary(data: ExportData) {
    if (!data.pdpmRankingAnalysis) return [];
    
    return [
      { 'Metric': 'Patient(s)', 'Value': data.patientNames || 'Unknown' },
      { 'Metric': 'Primary Diagnosis Recommendation', 'Value': typeof data.pdpmRankingAnalysis.primary_diagnosis_recommendation?.code === 'string' 
        ? data.pdpmRankingAnalysis.primary_diagnosis_recommendation.code 
        : data.pdpmRankingAnalysis.primary_diagnosis_recommendation?.code?.code || 'None' },
      { 'Metric': 'Primary Diagnosis Alternatives', 'Value': data.pdpmRankingAnalysis.primary_diagnosis_alternatives.length },
      { 'Metric': 'Secondary Diagnoses', 'Value': data.pdpmRankingAnalysis.non_primary_diagnoses.length },
      { 'Metric': 'NTA Qualifying Codes', 'Value': data.pdpmRankingAnalysis.nta_codes.length },
      { 'Metric': 'Total NTA Points', 'Value': data.pdpmRankingAnalysis.nta_codes.reduce((sum, item) => sum + item.nta_points, 0) },
      { 'Metric': 'SLP Related Codes', 'Value': data.pdpmRankingAnalysis.slp_codes.length },
      { 'Metric': 'Generated By', 'Value': data.generatedBy || 'CodePilot AI' },
      { 'Metric': 'Generated On', 'Value': new Date().toLocaleDateString() },
    ];
  }

  private prepareOverallSummary(data: ExportData) {
    const categoricalSummary = this.prepareCategoricalSummary(data);
    const pdpmSummary = data.pdpmRankingAnalysis ? this.preparePDPMSummary(data) : [];
    
    // Combine unique metrics
    const combinedSummary = [...categoricalSummary];
    
    pdpmSummary.forEach(pdpmItem => {
      if (!categoricalSummary.find(catItem => catItem.Metric === pdpmItem.Metric)) {
        combinedSummary.push(pdpmItem);
      }
    });
    
    return combinedSummary;
  }

  private convertToCSV(data: any[]): string {
    if (data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvHeaders = headers.join(',');
    
    const csvRows = data.map(row => 
      headers.map(header => {
        const value = row[header] || '';
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    );
    
    return [csvHeaders, ...csvRows].join('\n');
  }

  private autoSizeColumns(worksheet: XLSX.WorkSheet, data: any[]): void {
    if (data.length === 0) return;
    
    const colWidths: number[] = [];
    const headers = Object.keys(data[0]);
    
    // Initialize with header widths
    headers.forEach((header, index) => {
      colWidths[index] = header.length;
    });
    
    // Check data widths
    data.forEach(row => {
      headers.forEach((header, index) => {
        const cellValue = String(row[header] || '');
        const cellWidth = cellValue.length;
        if (cellWidth > colWidths[index]) {
          colWidths[index] = cellWidth;
        }
      });
    });
    
    // Set column widths (max 50 characters)
    worksheet['!cols'] = colWidths.map(width => ({ 
      wch: Math.min(width + 2, 50) 
    }));
  }

  private downloadFile(content: string, filename: string, mimeType: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private generateFileName(patientNames: string, reportType: string, extension: string): string {
    const timestamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const safeName = patientNames 
      ? patientNames.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_').slice(0, 30)
      : 'Patient';
    return `CodePilot_${reportType}_${safeName}_${timestamp}.${extension}`;
  }
}

export const exportService = new ExportService(); 